import { Badge } from '@understory-io/leaf/badge';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cx } from '~/styled-system/css';
import { ChoiceGroup, Choice } from '~/ui/choice-group';
import { ExpandIcon } from '~/ui/icons/expand';
import { LocationIcon } from '~/ui/icons/location';
import { Lightbox } from '~/ui/lightbox';
import { formatLocation } from '~/ui/formatted';
import type { ExperienceLocation } from '~/domain/experiences/index.server';

type MappableLocation = ExperienceLocation & {
  coordinates: NonNullable<ExperienceLocation['coordinates']>;
};

type LocationSectionProps = {
  locations: ExperienceLocation[];
};

const LiveMap = lazy(() => import('./live-map'));

export function LocationSection({ locations }: LocationSectionProps) {
  const mappable = useMemo<MappableLocation[]>(
    () =>
      locations.filter(
        (loc): loc is MappableLocation => loc.coordinates !== null
      ),
    [locations]
  );

  const [selectedId, setSelectedId] = useState<string | null>(
    mappable[0]?.id ?? null
  );
  const [inView, setInView] = useState(false);
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const [activated, setActivated] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const mapAreaRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  // On touch devices the overlay can't rely on hover — show it at all times
  // so the user always sees the call-to-action.
  useEffect(() => {
    const mq = window.matchMedia('(hover: none)');
    setIsTouch(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsTouch(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Defer mounting the live map until the section nears the viewport so we
  // don't burn Stadia credits for users who never scroll this far.
  useEffect(() => {
    const node = mapAreaRef.current;
    if (!node || inView) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  const selected = mappable.find((loc) => loc.id === selectedId) ?? mappable[0];

  // One place decides whether this section shows anything, and it decides
  // before the map mounts: Stadia bills per tile request, so a section with
  // nothing to show must never mount a map. Either no location has coordinates
  // to place on a map, or the basemap provider is failing so there is no map to
  // place one on. An outage is only knowable once the tile requests come back,
  // so that second case can drop the section after it has already rendered.
  const hasMappableLocation = locations.some((loc) => loc.coordinates !== null);
  if (!hasMappableLocation || !selected || mapUnavailable) return null;

  const isInteractive = mappable.length > 1;

  const showMapLabel = intl.formatMessage({
    defaultMessage: 'Click to enable',
    description:
      'Action that activates the interactive map on the experience details page',
  });
  const mapAriaLabel = intl.formatMessage(
    {
      defaultMessage: 'Map of {location}',
      description:
        'Accessible label for the interactive map on the experience details page',
    },
    { location: formatLocation(selected, 'full') }
  );
  const expandLabel = intl.formatMessage({
    defaultMessage: 'Click to expand',
    description: 'Action that opens the map in a fullscreen lightbox',
  });

  return (
    <Stack as="section" gap="medium">
      <Text as="h2" style="subheadline-large">
        <FormattedMessage
          defaultMessage="Location"
          description="Heading of the location section on the experience details page"
        />
      </Text>

      <ChoiceGroup
        aria-label={intl.formatMessage({
          defaultMessage: 'Select a location',
          description:
            'Accessible label for the location selector on the experience details page',
        })}
        interactive={isInteractive}
        value={selected.id}
        onChange={setSelectedId}
      >
        {mappable.map((loc) => (
          <Choice key={loc.id} value={loc.id} icon={<LocationIcon />}>
            {formatLocation(loc, 'full')}
          </Choice>
        ))}
      </ChoiceGroup>

      <div ref={mapAreaRef} className={mapAreaStyles}>
        {inView && !expanded && (
          <Suspense fallback={null}>
            <LiveMap
              latitude={selected.coordinates.latitude}
              longitude={selected.coordinates.longitude}
              ariaLabel={mapAriaLabel}
              onUnavailable={() => setMapUnavailable(true)}
            />
          </Suspense>
        )}
        {!activated && (
          <button
            type="button"
            onClick={() => setActivated(true)}
            className={cx(overlayStyles, isTouch && overlayAlwaysOnStyles)}
          >
            <span className={overlayPillStyles}>{showMapLabel}</span>
          </button>
        )}
        {activated && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className={expandButtonStyles}
          >
            <Badge appearance="neutral" icon={<ExpandIcon />}>
              {expandLabel}
            </Badge>
          </button>
        )}
      </div>

      <Lightbox
        isOpen={expanded}
        onClose={() => setExpanded(false)}
        ariaLabel={mapAriaLabel}
      >
        {expanded && (
          <Suspense fallback={null}>
            <LiveMap
              latitude={selected.coordinates.latitude}
              longitude={selected.coordinates.longitude}
              ariaLabel={mapAriaLabel}
              onUnavailable={() => setMapUnavailable(true)}
            />
          </Suspense>
        )}
      </Lightbox>
    </Stack>
  );
}

// Map area: rounded card with a fixed responsive height. Holds the live
// map and (until activation) a transparent overlay on top of it.
const mapAreaStyles = css({
  width: 'full',
  height: { base: '[240px]', tablet: '[280px]', desktop: '[320px]' },
  borderRadius: 'large',
  overflow: 'hidden',
  position: 'relative',
  background: 'surface.neutral',
});

// Overlay sitting on top of the live map. Intercepts pointer events so the
// map below stays inert (no scroll-zoom hijacking, accidental drags) until
// the user explicitly opts in. The dim + centered pill only appear on hover
// (mouse) or when forced on for touch devices via `overlayAlwaysOnStyles`.
const overlayStyles = css({
  position: 'absolute',
  inset: '[0]',
  // MapLibre's `.maplibregl-control-container` is z-index 2; sit above it so
  // the overlay also blocks the zoom in/out buttons until activation.
  zIndex: '[3]',
  width: 'full',
  height: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'interactive',
  transition: 'colors',
  '& > *': {
    opacity: '[0]',
    transition: 'colors',
  },
  _hover: {
    background: '[rgba(40, 60, 65, 0.45)]',
    '& > *': { opacity: '[1]' },
  },
});

const overlayAlwaysOnStyles = css({
  background: '[rgba(40, 60, 65, 0.45)]',
  '& > *': { opacity: '[1]' },
});

// Reset wrapper for the floating "Click to expand" badge: positions the
// Badge in the bottom-right of the activated map and adds the Figma drop
// shadow. All visual chrome (height, border, padding, typography) comes
// from the Leaf Badge inside.
const expandButtonStyles = css({
  position: 'absolute',
  bottom: 'medium',
  right: 'medium',
  zIndex: '[1]',
  border: 'none',
  padding: 'none',
  background: 'surface.base',
  borderRadius: 'full',
  cursor: 'interactive',
  boxShadow: '[0 4px 15px 0 rgba(14, 15, 12, 0.06)]',
  _focusVisible: {
    outline: 'medium',
    outlineColor: 'border.focus',
  },
});

// Centered call-to-action — small white pill matching the screenshot.
const overlayPillStyles = css({
  paddingX: 'medium',
  paddingY: 'small',
  borderRadius: 'full',
  background: 'surface.base',
  color: 'content.primary',
  fontWeight: '[600]',
  boxShadow: '[0 4px 15px 0 rgba(14, 15, 12, 0.12)]',
});
